import React, { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';

import { Typography } from 'lighthouse';

import { InfiniteScrollList } from '@/widgets/InfiniteScrollList';
import { ReviewCard } from '@/widgets/ReviewCard';

import { ReviewsSkeleton } from './components/ReviewSkeleton';

import { ReviewResponseType } from '@/client/services/review/models';
import { getReviewByStoreUUIDService } from '@/client/services/vendor/methods';
import { MessageText1 } from 'iconsax-react';

export const ReviewsTab = () => {
    const params = useParams();
    const { slug } = params;
    const [reviews, setReviews] = useState<ReviewResponseType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [last, setLast] = useState<boolean>(false);

    useEffect(() => {
        let isMounted = true;

        (async () => {
            setIsLoading(true);
            try {
                const res = await getReviewByStoreUUIDService(slug as string, {
                    page: 0,
                    size: 10
                });

                if (!isMounted) {
                    return;
                }

                setLast(res.data.last);
                setReviews(res?.data?.content ?? []);
            } catch {
                if (!isMounted) {
                    return;
                }

                setReviews([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [slug]);

    const getReviewByStoreUUID = async (page: number) => {
        try {
            const res = await getReviewByStoreUUIDService(slug as string, {
                page: page,
                size: 10
            });

            setLast(res.data.last);
            return res?.data?.content;
        } catch {
            return [];
        }
    };

    return (
        <div className='grid grid-cols-1 gap-3 p-3 sm:gap-4 sm:p-4'>
            {isLoading ? (
                <ReviewsSkeleton />
            ) : !reviews.length ? (
                <div className='flex flex-col items-center justify-center gap-3 rounded-lg bg-N50 px-4 py-16 text-center'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-white text-blackLowEmphasis shadow-elevation1'>
                        <MessageText1 size={24} color='currentcolor' aria-hidden />
                    </div>
                    <Typography variant='caption' color='blackLowEmphasis' className='block text-sm'>
                        هنوز نظری ثبت نشده است.
                    </Typography>
                </div>
            ) : (
                <InfiniteScrollList<ReviewResponseType>
                    fetchData={getReviewByStoreUUID}
                    isLastPage={last}
                    defaultItems={reviews}
                    renderRow={(review) => <ReviewCard key={review.id} review={review} />}
                    containerProps={{
                        className: 'flex flex-col gap-3'
                    }}
                    infiniteScrollProps={{
                        loader: (
                            <div className='flex flex-col gap-3'>
                                <ReviewsSkeleton />
                            </div>
                        )
                    }}
                />
            )}
        </div>
    );
};
